/**
 * preConnectCloudNewThreadRoute — the `/new` composer route for a pre-connect
 * (not-yet-provisioned, or not-yet-Forge-connected) cloud project.
 *
 * Clicking "+" or "open" on a cloud project row Forge has not connected to
 * yet must NOT provision a sprite — `connectCloudRuntime.ts`'s `ensure` call
 * is the only thing that does that, and it must only run once the user has
 * actually sent a message. This route sends the user to the composer landing
 * page with the project preselected instead; submitting the composer
 * (`NewThreadLanding.submission.ts` -> `connectCloudCatalogRow`) is the ONLY
 * place a cloud project provisions.
 *
 * Keyed on the stable CMMD (organization, project) identity
 * (`deriveCloudProjectKey`) rather than the runtime id: an unprovisioned
 * project has no runtime id yet, and a stopped one may be recycled to a new
 * one, but the CMMD project identity never changes.
 */
import { deriveCloudProjectKey } from "./cloudProjectsGroup.logic";
import type { SidebarPreConnectCloudMetadata } from "./SidebarProjectItem";

/**
 * The `execution` query param value on `/new` (route: apps/web/src/routes/_chat.new.tsx).
 *
 * Must stay equal to `CLOUD_EXECUTION_ID` in `../newThread/NewThreadLanding.logic.ts`,
 * which is what the composer matches against. It is duplicated rather than imported
 * on purpose: `newThread/` already imports from `sidebar/`, so importing back the
 * other way would close a circular dependency between the two directories. If the
 * two ever diverge the preselect silently falls back to the default execution target.
 *
 * Deliberately NOT exported — file-local use only (the dead-code gate flags an
 * unused export, and there is no second consumer).
 */
const PRE_CONNECT_CLOUD_NEW_THREAD_EXECUTION_ID = "cloud";

export interface PreConnectCloudNewThreadRoute {
  readonly to: "/new";
  readonly search: { readonly project: string; readonly execution: string };
}

export function resolvePreConnectCloudNewThreadRoute(
  preConnectCloud: SidebarPreConnectCloudMetadata,
): PreConnectCloudNewThreadRoute {
  return {
    to: "/new",
    search: {
      project: deriveCloudProjectKey(preConnectCloud),
      execution: PRE_CONNECT_CLOUD_NEW_THREAD_EXECUTION_ID,
    },
  };
}
